import {
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
  DEFAULT_THEME_MODE,
  THEME_MODE_STORAGE_KEY,
  THEME_PRIMARY_VAR,
  THEME_SECONDARY_VAR,
  THEME_STORAGE_KEY,
  ThemeMode
} from "constants/theme";

type ThemeColors = {
  primary?: string;
  secondary?: string;
};

const parseStoredColors = (rawValue: string | null): ThemeColors | null => {
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue);

    if (typeof parsed === "object" && parsed !== null) {
      return {
        primary: typeof parsed.primary === "string" ? parsed.primary : undefined,
        secondary: typeof parsed.secondary === "string" ? parsed.secondary : undefined
      };
    }
  } catch {
    // ignore
  }

  return null;
};

export const getStoredThemeColors = (): ThemeColors | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const rawValue = window.localStorage.getItem(THEME_STORAGE_KEY);
  return parseStoredColors(rawValue);
};

export const applyThemeColorsToDocument = (colors: ThemeColors) => {
  if (typeof document === "undefined") {
    return;
  }

  const { primary, secondary } = colors;

  document.documentElement.style.setProperty(
    THEME_PRIMARY_VAR,
    primary || DEFAULT_PRIMARY_COLOR
  );
  document.documentElement.style.setProperty(
    THEME_SECONDARY_VAR,
    secondary || DEFAULT_SECONDARY_COLOR
  );
};

export const bootstrapStoredThemeColors = () => {
  const storedColors = getStoredThemeColors();

  if (!storedColors) {
    return;
  }

  applyThemeColorsToDocument(storedColors);
};

export const getStoredThemeMode = (): ThemeMode => {
  if (typeof window === "undefined") {
    return DEFAULT_THEME_MODE;
  }

  const storedMode = window.localStorage.getItem(THEME_MODE_STORAGE_KEY) as ThemeMode | null;

  if (storedMode === "dark" || storedMode === "light") {
    return storedMode;
  }

  return DEFAULT_THEME_MODE;
};

export const applyThemeModeToDocument = (mode: ThemeMode) => {
  if (typeof document === "undefined") {
    return;
  }

  document.documentElement.dataset.theme = mode;
  document.documentElement.style.setProperty("color-scheme", mode);
};

export const persistThemeMode = (mode: ThemeMode) => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
};

export const bootstrapStoredThemeMode = () => {
  const mode = getStoredThemeMode();
  applyThemeModeToDocument(mode);
};

